import React, { createContext, useContext, useEffect, useState } from "react";

export const STR = {
  en: {
    meta: { title: "Neni’s Playhouse Childcare | Where Little Hearts Learn, Play & Grow!" },
    nav: {
      home: "Home", about: "About Us", programs: "Our Programs", gallery: "Gallery",
      resources: "Resources", contact: "Contact Us", enroll: "Enroll Now",
      callText: "Call or Text: (917) 496-0201",
    },
    hero: {
      badge: "Neni’s Playhouse Childcare",
      ages: "Ages 1 to 12",
      t1: "Where Little Hearts", t2: "Learn, Play & Grow!",
      sub: "Welcome to Neni’s Playhouse Childcare, where little minds blossom, friendships grow, and every day is filled with learning, laughter, and adventure!",
      cta1: "Schedule a Tour", cta2: "Explore Our Programs",
      photoLAlt: "Happy children playing together at the playhouse",
      photoRAlt: "Toddler playing happily",
    },
    about: {
      h1a: "All About ", h1b: "Me",
      welcome: "Welcome to Neni’s Playhouse Childcare! 🧸💕",
      hello: "Hello, families! My name is Ms. Jennifer, and I am so happy to welcome you to Neni’s Playhouse Childcare—a place where little smiles brighten our days, curious minds discover new things, and growing hearts are surrounded by love!",
      mom: "I am a proud mother of two and a caring childcare provider who believes every child deserves a safe, nurturing, and encouraging environment where they can learn, grow, and shine. Being a mom has taught me the importance of patience, compassion, understanding, and celebrating the little milestones that make childhood so special. My childcare experience has also helped shape my appreciation for creating positive, supportive experiences for children and their families.",
      degree: "I hold a Bachelor’s degree in Healthcare Administration, which has helped me develop valuable skills in leadership, organization, communication, and professionalism. I bring these skills into my approach to childcare, with a commitment to thoughtful planning, responsible care, and building trusting relationships with families.",
      photoAlt: "Happy child enjoying a book at the playhouse",
      ownerPhotoAlt: "Ms. Jennifer (Neni), owner of Neni’s Playhouse Childcare, smiling in the classroom",
      sections: [
        {
          emoji: "💖", title: "My Love for Learning and Little Learners",
          paras: [
            "At Neni’s Playhouse Childcare, I believe childhood should be filled with laughter, imagination, exploration, and exciting discoveries! Every child has their own unique personality, interests, and abilities, and I want each little learner to feel loved, valued, and encouraged to be themselves.",
            "Through play-based learning, creative activities, stories, music, and hands-on exploration, my goal is to help children discover the joy of learning while developing important skills. I strive to support each child’s social, emotional, cognitive, and physical development in ways that are appropriate for their age and individual needs.",
            "Whether we are creating a colorful masterpiece, discovering something new, practicing sharing, or celebrating a new accomplishment, I believe every day offers an opportunity to learn and grow!",
          ],
        },
        {
          emoji: "🧸", title: "A Safe Place to Play, Learn, and Grow",
          paras: [
            "Your child’s safety and well-being are among my most important priorities. I strive to create a warm, welcoming atmosphere where children feel comfortable, supported, and cared for.",
            "I believe in encouraging independence while providing the guidance children need to feel secure. I want little ones to build confidence, express their feelings, develop friendships, practice kindness, and learn to approach new experiences with curiosity.",
            "At Neni’s Playhouse Childcare, little hands stay busy, little minds keep growing, and little hearts learn the importance of respect, compassion, honesty, and gratitude.",
          ],
        },
        {
          emoji: "🤝", title: "Growing Together, One Little Step at a Time",
          paras: [
            "My goal is to partner with families to help each child feel confident, included, and excited to learn. I look forward to celebrating milestones, encouraging new skills, and creating meaningful memories along the way.",
            "I want Neni’s Playhouse Childcare to feel like a home away from home—a place where children can be themselves, families feel connected, and every little achievement is worth celebrating!",
          ],
        },
        {
          emoji: "🌟", title: "My Mission",
          paras: [
            "My mission is to provide a loving and engaging childcare environment where children are encouraged to explore, learn, create, and grow into confident little individuals. I am passionate about helping children build a strong foundation for their future while making their early years joyful, meaningful, and full of discovery.",
            "At Neni’s Playhouse Childcare, we play, we learn, we grow, and we shine together! 🌈💕",
          ],
        },
      ],
      thanks: "Thank you for considering Neni’s Playhouse Childcare for your family. I look forward to welcoming your little one into our childcare community and being part of their beautiful journey of growth and discovery!",
      sign: "— Ms. Jennifer, Owner",
    },
    why: {
      h: "Why Kids Love Us",
      photoAlt: "A happy little camper at Neni’s Playhouse",
      items: [
        { t: "Loving Care", d: "Every child deserves a warm, nurturing place where they feel safe to be themselves." },
        { t: "Creativity & Confidence", d: "Joyful experiences that spark creativity, build confidence, and inspire curiosity." },
        { t: "Real Friendships", d: "Little hearts grow together, blossoming into friendships that brighten every day." },
        { t: "Flexible Scheduling", d: "Early drop-off, late pick-up, weekend and overnight care available upon request." },
      ],
    },
    programs: {
      h: "Our Programs", enroll: "Enroll Now",
      overnight: { name: "Overnight Care", meta: "Available upon request", desc: "Overnight care is available upon request. Contact us to discuss your family’s overnight needs and availability." },
      items: [
        { name: "Full-Day Weekday Care", meta: "Mon – Fri · 8 AM – 5 PM", desc: "A joyful, nurturing day filled with play, learning, laughter, and adventure for your little one." },
        { name: "Early Drop-Off", meta: "Available upon request", desc: "Need to start your day early? Flexible early drop-off options are available upon request." },
        { name: "Late Pick-Up", meta: "Available upon request", desc: "Extended care beyond regular operating hours is available upon request for busy days." },
        { name: "Weekend Care", meta: "Upon request · Based on availability", desc: "Weekend childcare may be arranged based on availability and prior scheduling." },
      ],
    },
    flex: {
      h1: "Flexible Care for ", h2: "Busy Families",
      desc: "We understand that every family’s schedule is different. That’s why Neni’s Playhouse Childcare offers flexible childcare options, including early drop-off, late pick-up, and weekend care upon request. Contact us to discuss your family’s scheduling needs.",
      hoursLabel: "Regular Operating Hours", days: "Monday – Friday", hours: "8:00 AM – 5:00 PM",
      cards: [
        { t: "Early Drop-Off", d: "Flexible early drop-off options are available upon request." },
        { t: "Late Pick-Up", d: "Extended care beyond regular operating hours is available upon request." },
        { t: "Weekend Childcare", d: "Weekend childcare may be arranged based on availability and prior scheduling." },
      ],
      cta: "Contact Us for Availability",
    },
    join: {
      h1: "Join Our ", h2: "Playhouse Today!",
      bold: "Loving care, playful learning, and a place where your child truly belongs.",
      p: "At Neni’s Playhouse Childcare, we do more than just watch children — we nurture, teach, and celebrate every little milestone. From playful learning days to flexible care options like early drop-off, late pick-up, weekend and overnight care upon request, we’re here to make your family’s life easier. Click below to schedule a tour or ask a question — we can’t wait to welcome your family into ours!",
      cta: "Enroll Now", photoAlt: "A happy little camper playing at the playhouse",
    },
    reviews: {
      eyebrow: "Testimonials", h1: "What Parents ", h2: "Say About Us",
      sub: "Sweet words from the families we’re lucky to care for.",
      items: [
        { q: "Neni’s Playhouse has been a blessing for our family. Our daughter runs to the door every morning — she absolutely loves Ms. Jennifer and the playroom is every kid’s dream!", name: "Maria G.", role: "Mom of Sofia, 2" },
        { q: "The flexible hours saved us. Early drop-off and late pick-up fit our work schedule perfectly, and weekend care is a lifesaver.", name: "Danielle R.", role: "Mom of Liam, 4" },
        { q: "Our son learned his colors, letters, and so many songs in just a few months. Circle Time is his favorite part — every day is an adventure.", name: "Carlos M.", role: "Dad of Mateo, 3" },
        { q: "Ms. Jennifer treats every child like her own. We get updates and photos, and our little one is always smiling at pick-up.", name: "Ashley T.", role: "Mom of Ava, 1" },
        { q: "We needed overnight care during a family emergency and Neni’s Playhouse made it so easy. Truly caring, truly dependable people.", name: "Keisha W.", role: "Mom of Jayden, 7" },
        { q: "From the rainbow wall to the alphabet rug, the space is magical. Our twins ask to go back every single day!", name: "Lauren P.", role: "Mom of twins, 3" },
      ],
    },
    gallery: {
      eyebrow: "Gallery", h1: "Moments at ", h2: "Neni’s Playhouse",
      sub: "Little glimpses of the play, laughter, and learning that fill our days.",
      alts: [
        "Children playing with bubbles", "Kids playing a parachute game", "Children sitting in a circle game",
        "Baby playing with toys on the floor", "Child in imaginative costume play", "Girl enjoying a picture book",
        "Adult and child drawing together", "Wooden alphabet letters on the craft table",
      ],
    },
    contact: {
      eyebrow: "Contact Us", h1: "Let’s Start Your ", h2: "Little Camper’s Adventure!",
      callLabel: "Call or Text", emailLabel: "Email", hoursLabel: "Regular Hours",
      hoursVal: "Monday – Friday, 8:00 AM – 5:00 PM", addressLabel: "Address", addressVal: "Jamaica, Queens, New York", flexLabel: "Flexible Childcare",
      flexItems: [
        "Early Drop-Off: available upon request",
        "Late Pick-Up: available upon request",
        "Weekend Care: available upon request",
        "Overnight Care: available upon request",
      ],
      btnEmail: "Email Us", btnTour: "Schedule a Tour", btnEnroll: "Ask About Enrollment",
      labels: {
        name: "Parent/Guardian Name *", email: "Email Address *", phone: "Phone Number",
        age: "Child’s Age (1 – 12 years)", date: "Preferred Start Date", schedule: "Childcare Schedule Needed", message: "Message *",
      },
      ph: { name: "Your name", email: "you@email.com", phone: "(555) 000-0000", age: "Between 1 and 12 years old", msg: "Tell us about your little camper and what you need..." },
      schedules: [
        { slug: "regular-weekday-care", en: "Regular Weekday Care", es: "Cuidado Regular Entre Semana" },
        { slug: "early-drop-off", en: "Early Drop-Off", es: "Entrada Temprana" },
        { slug: "late-pick-up", en: "Late Pick-Up", es: "Salida Tardía" },
        { slug: "weekend-care", en: "Weekend Care", es: "Cuidado de Fin de Semana" },
        { slug: "overnight-care", en: "Overnight Care", es: "Cuidado Nocturno" },
        { slug: "other", en: "Other", es: "Otro" },
      ],
      err: { name: "Please tell us your name", email: "Please enter a valid email", message: "Please add a short message" },
      submit: "Send Inquiry", sending: "Sending...",
      okTitle: "Thank you!",
      okPre: "Your inquiry has been received. We’ll get back to you shortly — or reach us right away at ",
      okBtn: "Send Another Message",
      toastOk: "Thank you! We received your inquiry and will reach out soon.",
      toastErr: "Something went wrong — please call or text us at (917) 496-0201.",
    },
    footer: {
      h: "Let’s Get In Touch",
      blurb: "“Where Little Hearts Learn, Play & Grow!” We’d love to hear from you — whether you have questions, want to schedule a tour, or need enrollment info, we’re here to help every step of the way.",
      chips: { call: "Call or Text", email: "Email", hours: "Regular Hours", flex: "Flexible Childcare" },
      hoursVal: "Monday – Friday, 8:00 AM – 5:00 PM",
      flexVal: "Early Drop-Off, Late Pick-Up, Weekend & Overnight Care Available Upon Request",
    },
  },

  es: {
    meta: { title: "Neni’s Playhouse Childcare | ¡Donde los Corazoncitos Aprenden, Juegan y Crecen!" },
    nav: {
      home: "Inicio", about: "Sobre Nosotros", programs: "Nuestros Programas", gallery: "Galería",
      resources: "Recursos", contact: "Contáctenos", enroll: "Inscríbete Hoy",
      callText: "Llama o Textea: (917) 496-0201",
    },
    hero: {
      badge: "Neni’s Playhouse Childcare",
      ages: "De 1 a 12 años",
      t1: "Donde los Corazoncitos", t2: "Aprenden, Juegan y Crecen!",
      sub: "¡Bienvenidos a Neni’s Playhouse Childcare, donde las mentes florecen, las amistades crecen y cada día está lleno de aprendizaje, risas y aventuras!",
      cta1: "Agenda un Recorrido", cta2: "Explora Nuestros Programas",
      photoLAlt: "Niños felices jugando juntos en la casita de juegos",
      photoRAlt: "Niñito jugando feliz",
    },
    about: {
      h1a: "Todo Sobre ", h1b: "Mí",
      welcome: "¡Bienvenidos a Neni’s Playhouse Childcare! 🧸💕",
      hello: "¡Hola, familias! Mi nombre es Ms. Jennifer, ¡y estoy muy feliz de darles la bienvenida a Neni’s Playhouse Childcare, un lugar donde las sonrisitas alegran nuestros días, las mentes curiosas descubren cosas nuevas y los corazones que crecen están rodeados de amor!",
      mom: "Soy una orgullosa madre de dos y una proveedora de cuidado infantil cariñosa que cree que cada niño merece un ambiente seguro, nutritivo y alentador donde pueda aprender, crecer y brillar. Ser mamá me ha enseñado la importancia de la paciencia, la compasión, la comprensión y de celebrar los pequeños logros que hacen tan especial la niñez. Mi experiencia en el cuidado de niños también ha fortalecido mi aprecio por crear experiencias positivas y de apoyo para los niños y sus familias.",
      degree: "Tengo una licenciatura en Administración de Salud, que me ha ayudado a desarrollar valiosas habilidades en liderazgo, organización, comunicación y profesionalismo. Aplico estas habilidades en mi enfoque de cuidado infantil, con un compromiso de planificación cuidadosa, atención responsable y relaciones de confianza con las familias.",
      photoAlt: "Niña feliz leyendo un libro en la playhouse",
      ownerPhotoAlt: "Ms. Jennifer (Neni), dueña de Neni’s Playhouse Childcare, sonriendo en el salón",
      sections: [
        {
          emoji: "💖", title: "Mi Amor por el Aprendizaje y los Pequeños Aprendices",
          paras: [
            "En Neni’s Playhouse Childcare, ¡creo que la niñez debe estar llena de risas, imaginación, exploración y descubrimientos emocionantes! Cada niño tiene su propia personalidad, intereses y habilidades únicas, y quiero que cada pequeño aprendiz se sienta amado, valorado y animado a ser él mismo.",
            "A través del aprendizaje basado en el juego, actividades creativas, cuentos, música y exploración práctica, mi meta es ayudar a los niños a descubrir la alegría de aprender mientras desarrollan habilidades importantes. Me esfuerzo por apoyar el desarrollo social, emocional, cognitivo y físico de cada niño de manera apropiada para su edad y necesidades individuales.",
            "Ya sea creando una obra de arte colorida, descubriendo algo nuevo, practicando a compartir o celebrando un nuevo logro, ¡creo que cada día ofrece una oportunidad de aprender y crecer!",
          ],
        },
        {
          emoji: "🧸", title: "Un Lugar Seguro para Jugar, Aprender y Crecer",
          paras: [
            "La seguridad y el bienestar de su hijo están entre mis prioridades más importantes. Me esfuerzo por crear un ambiente cálido y acogedor donde los niños se sientan cómodos, apoyados y cuidados.",
            "Creo en fomentar la independencia mientras brindo la guía que los niños necesitan para sentirse seguros. Quiero que los pequeños desarrollen confianza, expresen sus sentimientos, hagan amistades, practiquen la amabilidad y se acerquen a nuevas experiencias con curiosidad.",
            "En Neni’s Playhouse Childcare, las manitas están ocupadas, las mentes siguen creciendo y los corazoncitos aprenden la importancia del respeto, la compasión, la honestidad y la gratitud.",
          ],
        },
        {
          emoji: "🤝", title: "Creciendo Juntos, Un Pasito a la Vez",
          paras: [
            "Mi meta es acompañar a las familias para que cada niño se sienta seguro, incluido y emocionado por aprender. Espero celebrar logros, animar nuevas habilidades y crear recuerdos significativos en el camino.",
            "Quiero que Neni’s Playhouse Childcare se sienta como un hogar lejos del hogar: un lugar donde los niños pueden ser ellos mismos, las familias se sienten conectadas y ¡cada pequeño logro vale la pena celebrarlo!",
          ],
        },
        {
          emoji: "🌟", title: "Mi Misión",
          paras: [
            "Mi misión es proporcionar un entorno de cuidado infantil amoroso y estimulante donde se anime a los niños a explorar, aprender, crear y crecer hacia pequeños individuos seguros de sí mismos. Me apasiona ayudar a los niños a construir una base sólida para su futuro mientras hago que sus primeros años sean alegres, significativos y llenos de descubrimiento.",
            "En Neni’s Playhouse Childcare, ¡jugamos, aprendemos, crecemos y brillamos juntos! 🌈💕",
          ],
        },
      ],
      thanks: "Gracias por considerar Neni’s Playhouse Childcare para su familia. ¡Espero darle la bienvenida a su pequeño en nuestra comunidad de cuidado infantil y ser parte de su hermoso camino de crecimiento y descubrimiento!",
      sign: "— Ms. Jennifer, Dueña",
    },
    why: {
      h: "Por Qué los Niños Nos Adoran",
      photoAlt: "Un pequeño feliz en Neni’s Playhouse",
      items: [
        { t: "Cariño y Amor", d: "Cada niño merece un lugar cálido y nutritivo donde sentirse seguro para ser él mismo." },
        { t: "Creatividad y Confianza", d: "Experiencias alegres que despiertan la creatividad, fortalecen la confianza e inspiran la curiosidad." },
        { t: "Amistades Verdaderas", d: "Los corazoncitos crecen juntos, floreciendo en amistades que alegran cada día." },
        { t: "Horarios Flexibles", d: "Entrada temprana, salida tardía, cuidado de fin de semana y nocturno, disponibles a petición." },
      ],
    },
    programs: {
      h: "Nuestros Programas", enroll: "Inscríbete Hoy",
      overnight: { name: "Cuidado Nocturno", meta: "Disponible a petición", desc: "El cuidado nocturno está disponible a petición. Contáctanos para hablar de las necesidades nocturnas de tu familia y su disponibilidad." },
      items: [
        { name: "Cuidado de Día Entre Semana", meta: "Lun – Vie · 8 AM – 5 PM", desc: "Un día alegre y nutritivo lleno de juego, aprendizaje, risas y aventuras para tu pequeño." },
        { name: "Entrada Temprana", meta: "Disponible a petición", desc: "¿Necesitas empezar tu día temprano? Hay opciones flexibles de entrada temprana disponibles a petición." },
        { name: "Salida Tardía", meta: "Disponible a petición", desc: "Cuidado extendido más allá del horario regular, disponible a petición para los días ocupados." },
        { name: "Cuidado de Fin de Semana", meta: "A petición · Según disponibilidad", desc: "El cuidado de fin de semana puede organizarse según disponibilidad y programación previa." },
      ],
    },
    flex: {
      h1: "Cuidado Flexible para ", h2: "Familias Ocupadas",
      desc: "Entendemos que el horario de cada familia es diferente. Por eso Neni’s Playhouse Childcare ofrece opciones de cuidado flexibles, incluyendo entrada temprana, salida tardía y cuidado de fin de semana a petición. ¡Contáctanos para hablar de las necesidades de horario de tu familia!",
      hoursLabel: "Horario Regular", days: "Lunes – Viernes", hours: "8:00 AM – 5:00 PM",
      cards: [
        { t: "Entrada Temprana", d: "Opciones flexibles de entrada temprana disponibles a petición." },
        { t: "Salida Tardía", d: "Cuidado extendido más allá del horario regular, disponible a petición." },
        { t: "Cuidado de Fin de Semana", d: "Puede organizarse según disponibilidad y programación previa." },
      ],
      cta: "Contáctanos para Disponibilidad",
    },
    join: {
      h1: "¡Únete a Nuestra ", h2: "Playhouse Hoy!",
      bold: "Cariño, aprendizaje a través del juego y un lugar donde tu pequeño realmente pertenece.",
      p: "En Neni’s Playhouse Childcare hacemos mucho más que cuidar niños: los nutrimos, enseñamos y celebramos cada pequeño logro. Desde días de aprendizaje divertido hasta opciones de cuidado flexibles como entrada temprana, salida tardía, cuidado de fin de semana y nocturno a petición, estamos aquí para facilitar la vida de tu familia. ¡Haz clic abajo para agendar un recorrido o hacer una pregunta: no podemos esperar para dar la bienvenida a tu familia en la nuestra!",
      cta: "Inscríbete Hoy", photoAlt: "Un pequeño feliz jugando en la playhouse",
    },
    reviews: {
      eyebrow: "Testimonios", h1: "Lo Que los Padres ", h2: "Dicen de Nosotros",
      sub: "Palabras cariñosas de las familias que tenemos la suerte de cuidar.",
      items: [
        { q: "Neni’s Playhouse ha sido una bendición para nuestra familia. Nuestra hija corre a la puerta cada mañana — ¡ama a la señorita Jennifer y la sala de juegos es el sueño de cualquier niño!", name: "Maria G.", role: "Mamá de Sofia, 2" },
        { q: "El horario flexible nos salvó. La entrada temprana y la salida tardía encajan perfecto con nuestro trabajo, y el cuidado de fin de semana es un salvavidas.", name: "Danielle R.", role: "Mamá de Liam, 4" },
        { q: "Nuestro hijo aprendió sus colores, letras y muchas canciones en solo unos meses. El Círculo de Cuento es su parte favorita — cada día es una aventura.", name: "Carlos M.", role: "Papá de Mateo, 3" },
        { q: "La señorita Jennifer trata a cada niño como si fuera suyo. Recibimos noticias y fotos, y nuestro pequeño siempre está sonriendo al recogerlo.", name: "Ashley T.", role: "Mamá de Ava, 1" },
        { q: "Necesitamos cuidado nocturno durante una emergencia familiar y Neni’s Playhouse lo hizo muy fácil. Gente genuinamente cariñosa y confiable.", name: "Keisha W.", role: "Mamá de Jayden, 7" },
        { q: "Desde el muro del arcoíris hasta la alfombra del alfabeto, el espacio es mágico. ¡Nuestros gemelos quieren volver todos los días!", name: "Lauren P.", role: "Mamá de gemelos, 3" },
      ],
    },
    gallery: {
      eyebrow: "Galería", h1: "Momentos en ", h2: "Neni’s Playhouse",
      sub: "Pequeños momentos de juego, risas y aprendizaje que llenan nuestros días.",
      alts: [
        "Niños jugando con burbujas", "Niños jugando con un paracaídas", "Niños sentados en un círculo",
        "Bebé jugando con juguetes en el piso", "Niño jugando a disfrazarse", "Niña disfrutando un cuento",
        "Adulto y niño dibujando juntos", "Letras de madera en la mesa de arte",
      ],
    },
    contact: {
      eyebrow: "Contáctenos", h1: "¡Comencemos la ", h2: "Aventura de tu Pequeño!",
      callLabel: "Llama o Envía un Texto", emailLabel: "Correo Electrónico", hoursLabel: "Horario Regular",
      hoursVal: "Lunes – Viernes, 8:00 AM – 5:00 PM", addressLabel: "Dirección", addressVal: "Jamaica, Queens, Nueva York", flexLabel: "Cuidado Flexible",
      flexItems: [
        "Entrada temprana: disponible a petición",
        "Salida tardía: disponible a petición",
        "Cuidado de fin de semana: disponible a petición",
        "Cuidado nocturno: disponible a petición",
      ],
      btnEmail: "Escríbenos", btnTour: "Agenda un Recorrido", btnEnroll: "Pregunta por Inscripción",
      labels: {
        name: "Nombre del Padre/Tutor *", email: "Correo Electrónico *", phone: "Número de Teléfono",
        age: "Edad del Niño (1 – 12 años)", date: "Fecha Preferida de Inicio", schedule: "Horario de Cuidado Necesario", message: "Mensaje *",
      },
      ph: { name: "Tu nombre", email: "tu@correo.com", phone: "(555) 000-0000", age: "Entre 1 y 12 años", msg: "Cuéntanos sobre tu pequeño y lo que necesitas..." },
      schedules: [
        { slug: "regular-weekday-care", en: "Regular Weekday Care", es: "Cuidado Regular Entre Semana" },
        { slug: "early-drop-off", en: "Early Drop-Off", es: "Entrada Temprana" },
        { slug: "late-pick-up", en: "Late Pick-Up", es: "Salida Tardía" },
        { slug: "weekend-care", en: "Weekend Care", es: "Cuidado de Fin de Semana" },
        { slug: "overnight-care", en: "Overnight Care", es: "Cuidado Nocturno" },
        { slug: "other", en: "Other", es: "Otro" },
      ],
      err: { name: "Por favor dinos tu nombre", email: "Por favor ingresa un correo válido", message: "Por favor agrega un mensaje corto" },
      submit: "Enviar Pregunta", sending: "Enviando...",
      okTitle: "¡Gracias!",
      okPre: "Hemos recibido tu pregunta. Te responderemos pronto — o llámanos ya al ",
      okBtn: "Enviar Otro Mensaje",
      toastOk: "¡Gracias! Recibimos tu pregunta y te contactaremos pronto.",
      toastErr: "Algo salió mal — por favor llama o envía un texto al (917) 496-0201.",
    },
    footer: {
      h: "Pongámonos En Contacto",
      blurb: "“¡Donde los Corazoncitos Aprenden, Juegan y Crecen!” Nos encantaría saber de ti: si tienes preguntas, quieres agendar un recorrido o necesitas información de inscripción, estamos aquí para ayudarte en cada paso.",
      chips: { call: "Llama o Envía un Texto", email: "Correo Electrónico", hours: "Horario Regular", flex: "Cuidado Flexible" },
      hoursVal: "Lunes – Viernes, 8:00 AM – 5:00 PM",
      flexVal: "Entrada Temprana, Salida Tardía, Cuidado de Fin de Semana y Cuidado Nocturno Disponibles a Petición",
    },
  },
};

const LangContext = createContext(null);

export function LangProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try {
      return localStorage.getItem("lang") === "es" ? "es" : "en";
    } catch {
      return "en";
    }
  });

  const setLang = (l) => {
    try {
      localStorage.setItem("lang", l);
    } catch {}
    setLangState(l);
  };

  useEffect(() => {
    document.documentElement.lang = lang === "es" ? "es" : "en";
    document.title = STR[lang].meta.title;
  }, [lang]);

  return <LangContext.Provider value={{ lang, setLang, s: STR[lang] }}>{children}</LangContext.Provider>;
}

export const useLang = () => useContext(LangContext);
